import { useState, useEffect, useMemo, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createApiKeySchema } from '../../../schemas/wallet/admin.schema';
import { Plus, Copy, Check } from 'lucide-react';
import DataTable from '../../../component/wallet/data-table/DataTable';
import StatusBadge from '../../../component/wallet/StatusBadge';
import { useAdmin } from '../../../Hooks/wallet/useadmin';
import { formatDate, maskApiKey, copyToClipboard } from '../../../lib/wallet/utils';
import { DEFAULT_PAGE_SIZE } from '../../../lib/wallet/constants';
import toast from 'react-hot-toast';

const columnHelper = createColumnHelper();

const ApiKeysPage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, fetchApiKeys, doCreateApiKey, doToggleApiKey } = useAdmin();
    const [apiKeys, setApiKeys] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_PAGE_SIZE, total_count: 0, total_pages: 1 });
    const [showCreate, setShowCreate] = useState(false);
    const [copiedId, setCopiedId] = useState(null);

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: zodResolver(createApiKeySchema),
    });

    const loadKeys = async (page = 1) => {
        try {
            const res = await fetchApiKeys({ page, limit: pagination.limit });
            setApiKeys(res?.data?.api_keys || []);
            setPagination((prev) => ({ ...prev, page, ...res?.data?.pagination }));
        } catch {
            // handled
        }
    };

    useEffect(() => {
        loadKeys();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCreate = async (data) => {
        try {
            await doCreateApiKey(data);
            setShowCreate(false);
            reset();
            loadKeys();
        } catch {
            // handled
        }
    };

    const handleToggle = async (key) => {
        try {
            await doToggleApiKey(key.id, { is_active: !key.is_active });
            loadKeys(pagination.page);
        } catch {
            // handled
        }
    };

    const handleCopy = async (apiKey, id) => {
        const ok = await copyToClipboard(apiKey);
        if (ok) {
            setCopiedId(id);
            toast.success('Copied!');
            setTimeout(() => setCopiedId(null), 2000);
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor('app_name', { header: 'App Name' }),
            columnHelper.accessor('api_key', {
                header: 'API Key',
                cell: (info) => (
                    <div className="d-flex align-items-center gap-2">
                        <code className="small">{maskApiKey(info.getValue())}</code>
                        <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => handleCopy(info.getValue(), info.row.original.id)}
                        >
                            {copiedId === info.row.original.id ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                        </button>
                    </div>
                ),
            }),
            columnHelper.accessor('is_active', {
                header: 'Status',
                cell: (info) => <StatusBadge status={info.getValue() ? 'active' : 'inactive'} />,
            }),
            columnHelper.accessor('created_at', {
                header: 'Created',
                cell: (info) => formatDate(info.getValue()),
            }),
            columnHelper.display({
                id: 'actions',
                header: 'Actions',
                cell: ({ row }) => (
                    <Form.Check
                        type="switch"
                        checked={row.original.is_active}
                        onChange={() => handleToggle(row.original)}
                        label={row.original.is_active ? 'Active' : 'Inactive'}
                    />
                ),
            }),
        ],
        [copiedId] // eslint-disable-line react-hooks/exhaustive-deps
    );

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">API Keys</h6>
            </div>
                <div className="fade-in">
            <div className="mb-3 text-end">
                <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
                    <Plus size={14} className="me-1" /> Create Key
                </Button>
            </div>

            <DataTable
                columns={columns}
                data={apiKeys}
                loading={loading}
                page={pagination.page}
                pageSize={pagination.limit}
                totalCount={pagination.total_count}
                totalPages={pagination.total_pages}
                onPageChange={(p) => loadKeys(p)}
                onPageSizeChange={(s) => setPagination((prev) => ({ ...prev, limit: s }))}
                emptyTitle="No API keys"
                emptyDescription="Create your first API key to get started."
            />

            {/* Create Modal */}
            <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create API Key</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit(handleCreate)}>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>App Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Restaurant App"
                                isInvalid={!!errors.app_name}
                                {...register('app_name')}
                            />
                            <Form.Control.Feedback type="invalid">{errors.app_name?.message}</Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Create'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
        </div>
        </div>
    );
};

export default ApiKeysPage;
